import React, { useEffect, useMemo, useState } from 'react';
import supabase from '../../config/supabase';
import {
  buildRevisionDiff,
  describeOrderRuleViolation,
  formatPoCurrency,
  formatPoQuantity,
  sendRevisedPurchaseOrder,
} from '../../services/purchaseOrderService';
import { validateOrderPackages } from '../../../../supabase/functions/_shared/parAndMoq.mjs';
import { resolvePoLinePackSize } from '../../utils/poLinePack';
import useHideMobileNav from '../../utils/useHideMobileNav';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const toDisplayNum = (v) => { const n = Number(v ?? 0); return n === 0 ? '' : String(n); };

const sanitizeNumericInput = (val) => {
  const s = String(val).replace(/[^\d.]/g, '');
  const parts = s.split('.');
  return parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : s;
};

const safeNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n >= 0 ? n : 0; };

const normalizeEditableItem = (raw, idx) => ({
  id: raw.id ?? `tmp-${idx}-${Date.now()}`,
  inventoryItemId: raw.inventory_item_id ?? raw.inventoryItemId ?? null,
  // The vendor-product identity. Editing quantity or notes must never erase
  // it — losing it is losing the line's MOQ/increment enforcement.
  purchaseOptionId: raw.purchase_option_id ?? raw.purchaseOptionId ?? null,
  description: raw.description || '',
  brand: raw.brand || '',
  location: raw.location || '',
  unit: raw.unit || '',
  // Read-only here: the pack describes the vendor's product, not this order.
  // It rides along so a revision re-sends the line as it was written instead
  // of dropping what one "case" meant.
  packSize: resolvePoLinePackSize(raw),
  orderQuantity: toDisplayNum(raw.order_quantity ?? raw.orderQuantity ?? 0),
  unitPrice: toDisplayNum(raw.unit_price ?? raw.unitPrice ?? 0),
  notes: raw.notes || '',
  _isNew: false,
});

const computeLineTotal = (qty, price) => Number((safeNum(qty) * safeNum(price)).toFixed(2));

const toServiceItem = (item) => ({
  inventoryItemId: item.inventoryItemId,
  purchaseOptionId: item.purchaseOptionId ?? null,
  description: item.description,
  brand: item.brand,
  location: item.location,
  unit: item.unit,
  packSize: item.packSize || '',
  orderQuantity: safeNum(item.orderQuantity),
  unitPrice: safeNum(item.unitPrice),
  lineTotal: computeLineTotal(item.orderQuantity, item.unitPrice),
  notes: item.notes,
});

export default function PurchaseOrderEditor({ purchaseOrder, storeName = '', userEmail = '', userName = '', onClose, onSent }) {
  useHideMobileNav(true);
  const originalItems = useMemo(() => (purchaseOrder?.items || []).map(normalizeEditableItem), [purchaseOrder]);
  const [editedItems, setEditedItems] = useState(originalItems);
  const [notes, setNotes] = useState(purchaseOrder?.notes || '');
  const [deliveryDate, setDeliveryDate] = useState(purchaseOrder?.requested_delivery_date || purchaseOrder?.requestedDeliveryDate || '');
  const [deliveryNotes, setDeliveryNotes] = useState(purchaseOrder?.delivery_notes || purchaseOrder?.deliveryNotes || '');
  const [recipientsDraft, setRecipientsDraft] = useState('');
  const [recipientOverrides, setRecipientOverrides] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // Linked vendor products for the lines that carry purchase_option_id, so a
  // revised quantity is judged against the vendor's real MOQ/increment before
  // it is sent. Non-fatal when unavailable — the server revalidates anyway.
  const [linkedOptionsById, setLinkedOptionsById] = useState({});
  useEffect(() => {
    const ids = [...new Set(originalItems.map((it) => it.purchaseOptionId).filter((id) => id != null))];
    if (!ids.length) { setLinkedOptionsById({}); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: optErr } = await supabase
          .from('purchase_options')
          .select('id, purchase_unit, moq_packages, order_increment, allows_fractional_packages')
          .in('id', ids);
        if (optErr) throw optErr;
        if (cancelled) return;
        const byId = {};
        (data || []).forEach((row) => { byId[String(row.id)] = row; });
        setLinkedOptionsById(byId);
      } catch (err) {
        console.warn('[PurchaseOrderEditor] linked options load failed (non-fatal):', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [originalItems]);

  const originalServiceItems = useMemo(() => originalItems.map(toServiceItem), [originalItems]);
  const editedServiceItems = useMemo(() => editedItems.map(toServiceItem), [editedItems]);

  const diff = useMemo(
    () => buildRevisionDiff(originalServiceItems, editedServiceItems),
    [originalServiceItems, editedServiceItems],
  );

  const oldSubtotal = useMemo(
    () => originalServiceItems.reduce((s, it) => s + Number(it.lineTotal || 0), 0),
    [originalServiceItems],
  );
  const newSubtotal = useMemo(
    () => editedServiceItems.reduce((s, it) => s + Number(it.lineTotal || 0), 0),
    [editedServiceItems],
  );

  const hasChanges = diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0;

  const updateItem = (id, field, value) => {
    setEditedItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };
  const removeItem = (id) => setEditedItems((prev) => prev.filter((it) => it.id !== id));
  const addItem = () => {
    setEditedItems((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        inventoryItemId: null,
        description: '',
        brand: '',
        location: '',
        unit: '',
        packSize: '',
        orderQuantity: '',
        unitPrice: '',
        notes: '',
        _isNew: true,
      },
    ]);
  };

  const addRecipient = () => {
    const cleaned = recipientsDraft.trim().toLowerCase();
    if (!cleaned) return;
    if (!isValidEmail(cleaned)) {
      setError(`"${recipientsDraft.trim()}" doesn't look like an email.`);
      return;
    }
    if (recipientOverrides.includes(cleaned)) {
      setRecipientsDraft('');
      return;
    }
    setRecipientOverrides((prev) => [...prev, cleaned]);
    setRecipientsDraft('');
    setError('');
  };
  const removeRecipient = (email) => setRecipientOverrides((prev) => prev.filter((e) => e !== email));

  const handleSendRevised = async () => {
    if (!hasChanges) {
      setError('No changes to send. Adjust at least one item first.');
      return;
    }
    const invalid = editedItems.find((it) => {
      const qty = safeNum(it.orderQuantity);
      const price = safeNum(it.unitPrice);
      return !it.description || !Number.isFinite(qty) || qty < 0 || !Number.isFinite(price) || price < 0;
    });
    if (invalid) {
      setError('Every line needs a description and non-negative quantity and price.');
      return;
    }
    // A revision may not violate the linked vendor product's ordering rules
    // silently — same authority as Savings Review and the server-side send
    // gate. Lines with no linked option carry no rules and pass untouched.
    const ruleViolation = editedItems.map((it) => {
      const option = it.purchaseOptionId != null ? linkedOptionsById[String(it.purchaseOptionId)] : null;
      if (!option) return null;
      const check = validateOrderPackages({
        packages: safeNum(it.orderQuantity),
        moqPackages: option.moq_packages,
        orderIncrement: option.order_increment,
        allowsFractional: option.allows_fractional_packages === true,
      });
      if (check.legal) return null;
      return `${it.description || 'A line'} ${describeOrderRuleViolation(check, option.purchase_unit)}. Change the quantity, remove the line, or edit this vendor product's ordering rules.`;
    }).find(Boolean);
    if (ruleViolation) {
      setError(ruleViolation);
      return;
    }
    setSending(true);
    setError('');
    try {
      const actorName = userName || userEmail || purchaseOrder.ordered_by_name || 'User';
      const actorEmail = userEmail || purchaseOrder.ordered_by_email || '';
      const revisedPurchaseOrder = {
        items: editedServiceItems,
        notes,
        contact: purchaseOrder.vendor_contact_snapshot,
        vendorName: purchaseOrder.vendor_name_snapshot,
        storeName: storeName || '',
        orderedBy: { name: actorName, email: actorEmail },
      };
      const result = await sendRevisedPurchaseOrder(
        purchaseOrder.id,
        revisedPurchaseOrder,
        {
          recipientsOverride: recipientOverrides.length ? recipientOverrides : undefined,
          requestedDeliveryDate: deliveryDate || null,
          deliveryNotes: deliveryNotes || null,
        },
      );
      if (typeof onSent === 'function') onSent(result);
    } catch (err) {
      setError(err?.message || 'Failed to send revision.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Edit purchase order and send revision">
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, color: '#bf360c', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Send Revision
            </div>
            <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, color: '#3D2518', fontSize: 18, marginTop: 2 }}>
              {purchaseOrder.po_number} · {purchaseOrder.vendor_name_snapshot}
            </div>
            <div style={{ color: '#8B7B73', fontSize: 13, marginTop: 4 }}>
              Edit lines below. The vendor will receive a REVISION email with new items in green and removed items struck through in red.
            </div>
          </div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close" disabled={sending}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <div style={{ overflowX: 'auto', border: '1px solid #E9DFD6', borderRadius: 10, marginBottom: 16 }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#FAF6F2' }}>
                  <th style={thStyle}>Item</th>
                  <th style={{ ...thStyle, width: '1%', whiteSpace: 'nowrap' }}>Pack / Size</th>
                  <th style={{ ...thStyle, width: '1%', whiteSpace: 'nowrap' }}>Order unit</th>
                  <th style={{ ...thStyle, textAlign: 'right', width: '1%', whiteSpace: 'nowrap' }}>Qty</th>
                  <th style={{ ...thStyle, textAlign: 'right', width: '1%', whiteSpace: 'nowrap' }}>Unit Price</th>
                  <th style={{ ...thStyle, textAlign: 'right', width: '1%', whiteSpace: 'nowrap' }}>Line Total</th>
                  <th style={{ ...thStyle, width: 32 }} aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {editedItems.map((item) => (
                  <tr key={item.id} style={{ background: item._isNew ? '#e8f5e9' : '#fff' }}>
                    <td style={tdStyle}>
                      <input style={inputStyle} value={item.description} onChange={(e) => updateItem(item.id, 'description', e.target.value)} placeholder="Description" />
                    </td>
                    <td style={{ ...tdStyle, width: '1%', whiteSpace: 'nowrap', color: '#8B7B73', fontSize: 12, paddingLeft: 10, paddingRight: 10 }}>
                      {item.packSize || '—'}
                    </td>
                    <td style={{ ...tdStyle, width: '1%', whiteSpace: 'nowrap' }}>
                      <input style={{ ...inputStyle, width: 56 }} value={item.unit} onChange={(e) => updateItem(item.id, 'unit', e.target.value)} placeholder="Unit" />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', width: '1%', whiteSpace: 'nowrap' }}>
                      <input
                        type="text" inputMode="decimal" style={{ ...inputStyle, textAlign: 'right', width: 56 }}
                        value={item.orderQuantity}
                        placeholder="0"
                        onChange={(e) => updateItem(item.id, 'orderQuantity', sanitizeNumericInput(e.target.value))}
                      />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', width: '1%', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #E9DFD6', borderRadius: 6, background: '#fff', overflow: 'hidden' }}>
                        <span style={{ padding: '4px 4px 4px 7px', fontSize: 13, color: '#8B7B73', userSelect: 'none', lineHeight: 1 }}>$</span>
                        <input
                          type="text" inputMode="decimal" style={{ ...inputStyle, width: 56, border: 'none', borderRadius: 0, paddingLeft: 2, textAlign: 'right' }}
                          value={item.unitPrice}
                          placeholder="0"
                          onChange={(e) => updateItem(item.id, 'unitPrice', sanitizeNumericInput(e.target.value))}
                        />
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', width: '1%', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {formatPoCurrency(computeLineTotal(item.orderQuantity, item.unitPrice))}
                    </td>
                    <td style={{ ...tdStyle, width: 32 }}>
                      <button type="button" style={removeBtnStyle} onClick={() => removeItem(item.id)} aria-label="Remove item">×</button>
                    </td>
                  </tr>
                ))}
                {editedItems.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#8B7B73' }}>No line items. Add one to send a revision.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addItem} style={addItemBtnStyle}>+ Add line item</button>

          <div style={diffSummaryStyle}>
            <div><strong style={{ color: '#1b5e20' }}>{diff.added.length}</strong> added</div>
            <div><strong style={{ color: '#b71c1c' }}>{diff.removed.length}</strong> removed</div>
            <div><strong style={{ color: '#9a6b00' }}>{diff.changed.length}</strong> changed</div>
            <div style={{ flex: 1 }} />
            <div>
              Previous: <strong>{formatPoCurrency(oldSubtotal)}</strong> →
              New: <strong>{formatPoCurrency(newSubtotal)}</strong>{' '}
              (<strong style={{ color: newSubtotal - oldSubtotal >= 0 ? '#1b5e20' : '#b71c1c' }}>
                {newSubtotal - oldSubtotal >= 0 ? '+' : ''}{formatPoCurrency(newSubtotal - oldSubtotal)}
              </strong>)
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle} htmlFor="editorDeliveryDate">Requested delivery date</label>
              <input id="editorDeliveryDate" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: '#8B7B73', marginTop: 3 }}>Optional — leave blank if unchanged.</div>
            </div>
            <div>
              <label style={labelStyle} htmlFor="editorDeliveryNotes">Delivery notes</label>
              <input id="editorDeliveryNotes" type="text" value={deliveryNotes} onChange={(e) => setDeliveryNotes(e.target.value)} placeholder="e.g. Deliver to back dock" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: '#8B7B73', marginTop: 3 }}>Optional — special delivery instructions.</div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={textareaStyle} placeholder="Any context to include with the revision…" />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Override recipients (optional)</label>
            <div style={{ color: '#8B7B73', fontSize: 12, marginBottom: 8 }}>
              By default the revision goes to the same recipients as the original PO. Add emails here to override.
            </div>
            <div style={chipRowStyle}>
              {recipientOverrides.map((email) => (
                <span key={email} style={chipStyle}>
                  {email}
                  <button type="button" onClick={() => removeRecipient(email)} style={chipRemoveStyle} aria-label={`Remove ${email}`}>×</button>
                </span>
              ))}
              <input
                type="email"
                placeholder={recipientOverrides.length ? 'Add another…' : 'name@vendor.com'}
                value={recipientsDraft}
                onChange={(e) => setRecipientsDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                    e.preventDefault();
                    addRecipient();
                  } else if (e.key === 'Backspace' && !recipientsDraft && recipientOverrides.length) {
                    setRecipientOverrides((prev) => prev.slice(0, -1));
                  }
                }}
                onBlur={addRecipient}
                style={chipInputStyle}
                disabled={sending}
              />
            </div>
          </div>

          {error && <div style={errorStyle}>{error}</div>}
        </div>

        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={cancelBtnStyle} disabled={sending}>Cancel</button>
          <button type="button" onClick={handleSendRevised} style={primaryBtnStyle} disabled={sending || !hasChanges}>
            {sending ? 'Sending revision…' : 'Send revised PO'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', paddingLeft: 16, paddingRight: 16 };
const modalStyle = { width: '100%', maxWidth: 920, maxHeight: 'calc(90vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))', background: '#fff', borderRadius: 16, boxShadow: '0 30px 60px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: 'Open Sans, sans-serif' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 22px', borderBottom: '1px solid #f0e6dd', background: '#fdfaf6' };
const closeBtnStyle = { border: 'none', background: 'transparent', fontSize: 28, lineHeight: 1, color: '#8B7B73', cursor: 'pointer' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { padding: '8px 10px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5A4A42', borderBottom: '1px solid #E9DFD6' };
const tdStyle = { padding: '4px 6px', borderBottom: '1px solid #f3ebe2', verticalAlign: 'middle' };
const inputStyle = { width: '100%', padding: '4px 6px', border: '1px solid #E9DFD6', borderRadius: 6, fontSize: 13, background: '#fff', color: '#3D2518', lineHeight: '1.2' };
const removeBtnStyle = { border: '1px solid #d8cdc3', background: '#fff', color: '#b71c1c', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const addItemBtnStyle = { background: '#fff', border: '1.5px dashed #d8cdc3', color: '#5A4A42', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' };
const diffSummaryStyle = { marginTop: 16, display: 'flex', gap: 14, padding: '10px 12px', background: '#FAF6F2', borderRadius: 10, fontSize: 13, alignItems: 'center', flexWrap: 'wrap' };
const labelStyle = { display: 'block', fontWeight: 700, color: '#5A4A42', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 };
const textareaStyle = { width: '100%', padding: '8px 10px', border: '1.5px solid #E9DFD6', borderRadius: 10, fontSize: 13, background: '#fff', color: '#3D2518', fontFamily: 'inherit', resize: 'vertical' };
const chipRowStyle = { display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8, border: '1.5px solid #E9DFD6', borderRadius: 10, background: '#FAF6F2', minHeight: 48 };
const chipStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#fff', border: '1px solid #d8cdc3', borderRadius: 999, fontSize: 13, color: '#3D2518' };
const chipRemoveStyle = { background: 'transparent', border: 'none', color: '#8B7B73', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 };
const chipInputStyle = { flex: 1, minWidth: 160, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#3D2518' };
const footerStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 22px', borderTop: '1px solid #f0e6dd', background: '#fdfaf6' };
const cancelBtnStyle = { background: '#fff', border: '1px solid #d8cdc3', color: '#5A4A42', borderRadius: 10, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' };
const primaryBtnStyle = { background: '#bf360c', border: '1px solid #8a2700', color: '#fff', borderRadius: 10, padding: '8px 18px', fontWeight: 700, cursor: 'pointer' };
const errorStyle = { color: '#b71c1c', background: '#fdecea', padding: '10px 12px', borderRadius: 8, marginTop: 14, fontSize: 13 };
